/**
 * VerifyMoreNudge — subtle banner shown on a course page ONLY to:
 *   - a signed-in user
 *   - who is an approved claim-owner of this degree's university
 *   - when fewer than 5 fields have provenance on this page
 *
 * Dismissible per session — nagging the same admin on every page
 * they open is a great way to get them to unclaim. sessionStorage
 * keyed by degree id so a fresh visit to another course still shows.
 *
 * Reads audit map from `DegreePageContext` (populated once per page
 * load by `DegreePageProvider`), so no extra fetch is required.
 */
import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { DegreePageContext } from "@/components/app/degree/DegreeDetailBits";

const VERIFIED_TARGET = 5;

export default function VerifyMoreNudge({ degreeId, universityId }) {
  const { user } = useAuth();
  const ctx = useContext(DegreePageContext);
  const auditByField = ctx?.auditByField;
  const [isOwner, setIsOwner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Session-scoped dismissal — respect the user's earlier "not now".
    try {
      const key = `wua_verify_nudge_dismissed_${degreeId}`;
      if (sessionStorage.getItem(key) === "1") setDismissed(true);
    } catch {
      /* ignore private-mode */
    }
  }, [degreeId]);

  useEffect(() => {
    let cancelled = false;
    if (!user || !universityId) {
      setIsOwner(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/claims/mine");
        const items = data.items || [];
        const owned = items.some(
          (c) => c.status === "approved" && c.university_id === universityId,
        );
        if (!cancelled) setIsOwner(owned);
      } catch {
        if (!cancelled) setIsOwner(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, universityId]);

  const verifiedCount = useMemo(() => {
    if (!auditByField) return null;
    // Only count university-verified rows — staff-with-source chips
    // don't count towards the university's "verified" quota.
    let n = 0;
    for (const row of auditByField.values()) {
      if (row.actor_type === "university") n += 1;
    }
    return n;
  }, [auditByField]);

  const show =
    isOwner &&
    !dismissed &&
    verifiedCount !== null &&
    verifiedCount < VERIFIED_TARGET;

  if (!show) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(`wua_verify_nudge_dismissed_${degreeId}`, "1");
    } catch { /* ignore */ }
  };

  return (
    <div
      className="mt-4 mb-6 border border-emerald-300 bg-emerald-50/70 rounded-lg px-4 py-3 flex items-center gap-3"
      data-testid="verify-more-nudge"
    >
      <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-emerald-900">
          <strong className="font-semibold">
            {verifiedCount} of {VERIFIED_TARGET} recommended fields verified
          </strong>{" "}
          on this course. Verifying more helps families trust your page.
        </div>
        <div className="mt-1 h-1.5 w-full max-w-xs bg-emerald-100 rounded overflow-hidden">
          <div
            className="h-full bg-emerald-600"
            style={{
              width: `${Math.min(100, (verifiedCount / VERIFIED_TARGET) * 100)}%`,
            }}
          />
        </div>
      </div>
      <Link
        to={`/uni-portal/degree/${degreeId}`}
        className="text-[12px] font-underground-sc tracking-wider text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1 shrink-0"
        data-testid="verify-more-nudge-cta"
      >
        Verify more <ArrowRight className="h-3 w-3" />
      </Link>
      <button
        type="button"
        onClick={handleDismiss}
        className="text-stone-400 hover:text-stone-700 shrink-0"
        data-testid="verify-more-nudge-dismiss"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
